import { useSearchParams } from 'react-router-dom';
import { formatOcrConfidence } from '@/utils/kycDisplay';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Select,
  Card,
  Typography,
  Drawer,
  Form,
  Input,
  Checkbox,
  Descriptions,
  App,
} from 'antd';
import { DatePicker } from 'antd';
import { ReloadOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import {
  ManualReviewTask,
  KYCReviewTaskStatus,
  KYCReviewReasonCode,
  KYCReviewMissingField,
  ResolveManualReviewRequest,
  RejectManualReviewRequest,
} from '@mygate/common';
import { ColumnsType } from 'antd/es/table';
import { kycReviewTasksService } from '@/services/kycReviewTasks';
import { formatDateTime } from '@/utils/date';
import { Dayjs } from 'dayjs';

interface ReviewFormValues {
  documentNumber: string;
  dateOfIssue: Dayjs;
  identityConfirmed: boolean;
  note?: string;
}

const { Text } = Typography;

const STATUS_COLORS: Record<KYCReviewTaskStatus, string> = {
  [KYCReviewTaskStatus.OPEN]: 'warning',
  [KYCReviewTaskStatus.IN_PROGRESS]: 'processing',
  [KYCReviewTaskStatus.RESOLVED]: 'success',
  [KYCReviewTaskStatus.REJECTED]: 'error',
};

const REASON_LABELS: Record<KYCReviewReasonCode, string> = {
  [KYCReviewReasonCode.OCR_MRZ_FAILURE]: 'OCR/MRZ Failure',
  [KYCReviewReasonCode.MISSING_REQUIRED_FIELD]: 'Missing Field',
  [KYCReviewReasonCode.LOW_CONFIDENCE]: 'Low Confidence',
  [KYCReviewReasonCode.POSTIDENT_MISMATCH]: 'PostIdent Mismatch',
};

const MISSING_FIELD_LABELS: Record<KYCReviewMissingField, string> = {
  documentNumber: 'Document No.',
  dateOfIssue: 'Date of Issue',
};

export function KYCManualReviewPage() {
  const { message, modal } = App.useApp();
  const [searchParams] = useSearchParams();

  // ── list state ─────────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState<ManualReviewTask[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<KYCReviewTaskStatus>(
    () =>
      Object.values(KYCReviewTaskStatus).find(status => status === searchParams.get('status')) ??
      KYCReviewTaskStatus.OPEN,
  );
  const [pagination, setPagination] = useState({ page: 1, limit: 20 });

  // ── review drawer state ────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ManualReviewTask | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [form] = Form.useForm<ReviewFormValues>();

  // ── guard: don't re-fetch on first render via the pagination useEffect ─────
  const initializedRef = useRef(false);

  // ── refs always reflecting latest filter/pagination ──────────────────────
  const statusFilterRef = useRef(statusFilter);
  const paginationRef = useRef(pagination);
  statusFilterRef.current = statusFilter;
  paginationRef.current = pagination;

  // ── stable fetch (reads from refs to avoid stale closure) ─────────────────
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const result = await kycReviewTasksService.fetchReviewTasks({
        status: statusFilterRef.current,
        page: paginationRef.current.page,
        limit: paginationRef.current.limit,
      });
      if (result) {
        setTasks(result.items);
        setTotal(result.total);
      }
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to load review tasks');
    } finally {
      setLoading(false);
    }
  }, []); // stable — reads from refs

  // ── initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetchTasks();
    initializedRef.current = true;
  }, []);

  // ── refetch when pagination changes (after first load) ────────────────────
  useEffect(() => {
    if (initializedRef.current) {
      fetchTasks();
    }
  }, [pagination.page, pagination.limit]);

  // ── status filter handler — resets page or fetches directly ───────────────
  const handleStatusChange = (value: KYCReviewTaskStatus) => {
    setStatusFilter(value);
    statusFilterRef.current = value;
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
      // useEffect on pagination.page will trigger fetchTasks with updated ref
    } else {
      fetchTasks();
    }
  };

  // ── drawer open/close ──────────────────────────────────────────────────────
  const openDrawer = (task: ManualReviewTask) => {
    setSelectedTask(task);
    form.resetFields();
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedTask(null);
    form.resetFields();
  };

  // ── PDF download ───────────────────────────────────────────────────────────
  const handleDownloadPdf = async () => {
    if (!selectedTask) return;
    setPdfDownloading(true);
    try {
      const blob = await kycReviewTasksService.downloadResultPdf(selectedTask.kycCaseId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kyc_result_${selectedTask.kycCaseId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('PDF downloaded successfully');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to download PDF');
    } finally {
      setPdfDownloading(false);
    }
  };

  // ── resolve task ───────────────────────────────────────────────────────────
  const handleResolve = async (values: ReviewFormValues) => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      const body: ResolveManualReviewRequest = {
        documentNumber: values.documentNumber,
        dateOfIssue: values.dateOfIssue.format('YYYY-MM-DD'),
        identityConfirmed: values.identityConfirmed,
        note: values.note,
      };
      await kycReviewTasksService.resolveTask(selectedTask.id, body);
      message.success('Task resolved successfully');
      closeDrawer();
      fetchTasks();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to resolve task');
    } finally {
      setSubmitting(false);
    }
  };

  // ── reject task ────────────────────────────────────────────────────────────
  const handleReject = () => {
    if (!selectedTask) return;
    let reason = '';
    modal.confirm({
      title: 'Reject Manual Review Task',
      content: (
        <div>
          <p>Provide a rejection reason:</p>
          <Input.TextArea
            rows={3}
            placeholder="e.g. Document expired, face does not match, …"
            onChange={e => (reason = e.target.value)}
            style={{ marginTop: 8 }}
          />
        </div>
      ),
      okText: 'Reject',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!reason.trim()) {
          message.error('Please provide a rejection reason');
          return Promise.reject();
        }
        const body: RejectManualReviewRequest = { reason: reason.trim() };
        try {
          await kycReviewTasksService.rejectTask(selectedTask.id, body);
          message.success('Task rejected');
          closeDrawer();
          fetchTasks();
        } catch (err) {
          message.error(err instanceof Error ? err.message : 'Failed to reject task');
          return Promise.reject();
        }
      },
    });
  };

  // ── table columns ──────────────────────────────────────────────────────────
  const columns: ColumnsType<ManualReviewTask> = useMemo(
    () => [
      {
        title: 'Prüffall',
        key: 'case',
        width: 200,
        render: (_, record) => (
          <div className="table-cell-stack">
            <Text copyable={{ text: record.kycCaseId }}>Fall {record.kycCaseId.slice(-8)}</Text>
            <Text className="table-cell-muted" copyable={{ text: record.userId }}>
              Kunde {record.userId.slice(-8)}
            </Text>
            <Text className="table-cell-muted">{formatDateTime(record.createdAt)}</Text>
          </div>
        ),
      },
      {
        title: 'Reasons',
        dataIndex: 'reasons',
        key: 'reasons',
        render: (reasons: KYCReviewReasonCode[]) => (
          <Space wrap size={4}>
            {reasons.map(r => (
              <Tag key={r} color="orange" style={{ fontSize: 11 }}>
                {REASON_LABELS[r] ?? r}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        title: 'Missing Fields',
        dataIndex: 'missingFields',
        key: 'missingFields',
        width: 160,
        render: (fields?: KYCReviewMissingField[]) =>
          fields && fields.length > 0 ? (
            <Space wrap size={4}>
              {fields.map(f => (
                <Tag key={f} color="red" style={{ fontSize: 11 }}>
                  {MISSING_FIELD_LABELS[f] ?? f}
                </Tag>
              ))}
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: 'OCR Conf.',
        dataIndex: 'ocrConfidence',
        key: 'ocrConfidence',
        width: 100,
        align: 'right' as const,
        // Highlight by the server's own verdict: the threshold is KYC_OCR_CONFIDENCE_THRESHOLD on the backend,
        // so a number hard-coded here would drift from it.
        render: (v: number | undefined, task: ManualReviewTask) =>
          v != null ? (
            <Text type={task.reasons.includes(KYCReviewReasonCode.LOW_CONFIDENCE) ? 'danger' : undefined}>
              {formatOcrConfidence(v)}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 145,
        render: (status: KYCReviewTaskStatus) => (
          <Tag title={status} color={STATUS_COLORS[status]}>
            {status}
          </Tag>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        fixed: 'right' as const,
        render: (_: unknown, record: ManualReviewTask) => (
          <Button
            type="primary"
            size="small"
            disabled={record.status === KYCReviewTaskStatus.RESOLVED || record.status === KYCReviewTaskStatus.REJECTED}
            onClick={() => openDrawer(record)}
          >
            Review
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Offene Identitätsprüfungen bearbeiten.</Typography.Text>
        <Button icon={<ReloadOutlined />} onClick={fetchTasks}>
          Refresh
        </Button>
      </div>

      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Filters */}
          <Space wrap>
            <Select<KYCReviewTaskStatus>
              value={statusFilter}
              onChange={handleStatusChange}
              style={{ width: 160 }}
              options={Object.values(KYCReviewTaskStatus).map(s => ({ label: s, value: s }))}
            />
          </Space>

          {/* Table */}
          <Table<ManualReviewTask>
            columns={columns}
            dataSource={tasks}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total,
              showSizeChanger: true,
              showTotal: t => `Total ${t} tasks`,
              onChange: (page, pageSize) => setPagination({ page, limit: pageSize }),
            }}
            tableLayout="fixed"
            scroll={{ x: 905 }}
          />
        </Space>
      </Card>

      {/* Review Drawer */}
      <Drawer
        title="Review KYC Task"
        placement="right"
        width={520}
        open={drawerOpen}
        onClose={closeDrawer}
        destroyOnClose
        extra={
          <Button icon={<DownloadOutlined />} loading={pdfDownloading} onClick={handleDownloadPdf}>
            Download PDF
          </Button>
        }
      >
        {selectedTask && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Task summary */}
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Task ID">
                <Text copyable={{ text: selectedTask.id }} style={{ fontSize: 12 }}>
                  {selectedTask.id}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="User ID">
                <Text copyable={{ text: selectedTask.userId }} style={{ fontSize: 12 }}>
                  {selectedTask.userId}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="KYC Case ID">
                <Text copyable={{ text: selectedTask.kycCaseId }} style={{ fontSize: 12 }}>
                  {selectedTask.kycCaseId}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLORS[selectedTask.status]}>{selectedTask.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Reasons">
                <Space wrap size={4}>
                  {selectedTask.reasons.map(r => (
                    <Tag key={r} color="orange" style={{ fontSize: 11 }}>
                      {REASON_LABELS[r] ?? r}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
              {selectedTask.missingFields && selectedTask.missingFields.length > 0 && (
                <Descriptions.Item label="Missing Fields">
                  <Space wrap size={4}>
                    {selectedTask.missingFields.map(f => (
                      <Tag key={f} color="red" style={{ fontSize: 11 }}>
                        {MISSING_FIELD_LABELS[f] ?? f}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
              {selectedTask.ocrConfidence != null && (
                <Descriptions.Item label="OCR Confidence">
                  {formatOcrConfidence(selectedTask.ocrConfidence)}
                </Descriptions.Item>
              )}
              {selectedTask.details && selectedTask.details.length > 0 && (
                <Descriptions.Item label="Details">
                  <Space direction="vertical" size={2}>
                    {selectedTask.details.map((d, i) => (
                      <Text key={i} style={{ fontSize: 12 }}>
                        {d}
                      </Text>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* Resolve form */}
            <Form form={form} layout="vertical" onFinish={handleResolve}>
              <Form.Item
                name="documentNumber"
                label="Document Number"
                rules={[{ required: true, message: 'Document number is required' }]}
              >
                <Input placeholder="e.g. L01X00T47" />
              </Form.Item>

              <Form.Item
                name="dateOfIssue"
                label="Date of Issue"
                rules={[{ required: true, message: 'Date of issue is required' }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="YYYY-MM-DD" />
              </Form.Item>

              <Form.Item
                name="identityConfirmed"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value ? Promise.resolve() : Promise.reject(new Error('Identity confirmation is required')),
                  },
                ]}
              >
                <Checkbox>Identity confirmed — the document photo matches the applicant</Checkbox>
              </Form.Item>

              <Form.Item name="note" label="Note (optional)">
                <Input.TextArea rows={2} placeholder="Any additional notes for the audit trail…" />
              </Form.Item>

              <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={handleReject}
                  disabled={
                    selectedTask.status === KYCReviewTaskStatus.RESOLVED ||
                    selectedTask.status === KYCReviewTaskStatus.REJECTED
                  }
                >
                  Reject
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<CheckCircleOutlined />}
                  loading={submitting}
                  disabled={
                    selectedTask.status === KYCReviewTaskStatus.RESOLVED ||
                    selectedTask.status === KYCReviewTaskStatus.REJECTED
                  }
                >
                  Resolve
                </Button>
              </Space>
            </Form>
          </Space>
        )}
      </Drawer>
    </>
  );
}
